import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { recordSupportDeskEvent } from "../api/supportApi";
import { getActiveSession } from "../utils/nightStayAccount";
import { isSupportEntryEnabled } from "../utils/supportRollout";
import { useWebsiteConfig } from "./WebsiteConfigContext";
import useSupportConversationState from "../Components/Support/useSupportConversationState";

const SupportDeskContext = createContext({
  isOpen: false,
  bookingId: "",
  sourceContext: "floating_widget",
  rollout: null,
  openSupportDesk: () => {},
  closeSupportDesk: () => {},
  isEntryEnabled: () => false,
  trackSupportEvent: async () => null,
  trackSupportEntry: async () => null,
});

const normalizeDeskValue = (value) => String(value || "").trim();

export const SupportDeskProvider = ({ children }) => {
  const { supportRollout } = useWebsiteConfig();
  const conversations = useSupportConversationState();
  const { setNavigation: setConversationNavigation } = conversations;
  const [deskState, setDeskState] = useState({
    isOpen: false,
    bookingId: "",
    sourceContext: "floating_widget",
  });

  const isEntryEnabled = useCallback(
    ({ sourceContext = "support_page" } = {}) => isSupportEntryEnabled(supportRollout, sourceContext),
    [supportRollout]
  );

  const trackSupportEvent = useCallback(async ({
    eventType = "desk_opened",
    sourceContext = "",
    bookingId = "",
    referenceCode = "",
    context = {},
  } = {}) => {
    if (!getActiveSession()) return null;
    try {
      return await recordSupportDeskEvent({
        eventType: normalizeDeskValue(eventType) || "desk_opened",
        bookingId: normalizeDeskValue(bookingId || referenceCode),
        sourceContext: normalizeDeskValue(sourceContext),
        context: {
          pageTitle: typeof document !== "undefined" ? document.title : "",
          path:
            typeof window !== "undefined"
              ? `${window.location.pathname || "/"}${window.location.search || ""}`
              : "",
          ...(normalizeDeskValue(context?.option) ? { option: normalizeDeskValue(context.option) } : {}),
          ...(normalizeDeskValue(context?.reason) ? { reason: normalizeDeskValue(context.reason) } : {}),
        },
      });
    } catch (_) {
      return null;
    }
  }, []);

  const trackSupportEntry = useCallback(
    (options = {}) => trackSupportEvent({ ...options, eventType: "desk_opened" }),
    [trackSupportEvent]
  );

  const openSupportDesk = useCallback((options = {}) => {
    const bookingId = normalizeDeskValue(options.bookingId || options.referenceCode);
    const sourceContext =
      normalizeDeskValue(options.sourceContext) || (bookingId ? "booking_card" : "floating_widget");
    if (!isSupportEntryEnabled(supportRollout, sourceContext)) {
      return;
    }
    setDeskState({
      isOpen: true,
      bookingId,
      sourceContext,
    });
    if (options.ticketId) setConversationNavigation({ view: "conversation", ticketId: options.ticketId, bookingId });
    else if (bookingId) setConversationNavigation({ view: "new", ticketId: "", bookingId });
    trackSupportEntry({ bookingId, sourceContext });
  }, [supportRollout, trackSupportEntry, setConversationNavigation]);

  const closeSupportDesk = useCallback(() => {
    setDeskState((prev) => ({
      ...prev,
      isOpen: false,
    }));
  }, []);

  const value = useMemo(
    () => ({
      ...deskState,
      conversations,
      rollout: supportRollout,
      openSupportDesk,
      closeSupportDesk,
      isEntryEnabled,
      trackSupportEvent,
      trackSupportEntry,
    }),
    [closeSupportDesk, conversations, deskState, isEntryEnabled, openSupportDesk, supportRollout, trackSupportEntry, trackSupportEvent]
  );

  return <SupportDeskContext.Provider value={value}>{children}</SupportDeskContext.Provider>;
};

export const useSupportDesk = () => useContext(SupportDeskContext);
